import { useMemo } from "react";
import useSWR from "swr";
// utils
import { fetcher, endpoints } from "utils/axios";

const SWR_OPTIONS = { shouldRetryOnError: false };

// ----------------------------------------------------------------------

export function useGetProcesses() {
  const URL = endpoints.processes.list;
  const {
    data, isLoading, error, isValidating, mutate,
  } = useSWR(URL, fetcher, SWR_OPTIONS);

  return useMemo(
    () => ({
      processes: data || [],
      processesLoading: isLoading,
      processesError: error,
      processesValidating: isValidating,
      processesEmpty: !isLoading && !(data && data.length),
      processesRefetch: mutate,
    }),
    [data, error, isLoading, isValidating, mutate]
  );
}

// ----------------------------------------------------------------------

export function useGetConfigs(processCode) {
  const URL = endpoints.configs.list(processCode);
  const {
    data, isLoading, error, isValidating, mutate,
  } = useSWR(URL, fetcher, SWR_OPTIONS);

  return useMemo(
    () => ({
      configs: data || [],
      configsLoading: isLoading,
      configsError: error,
      configsValidating: isValidating,
      configsEmpty: !isLoading && !(data && data.length),
      configsRefetch: mutate,
    }),
    [data, error, isLoading, isValidating, mutate]
  );
}

// ----------------------------------------------------------------------

export function useGetEstimatorsList() {
  const URL = endpoints.estimators.list;
  const {
    data, isLoading, error, isValidating, mutate,
  } = useSWR(URL, fetcher, SWR_OPTIONS);

  return useMemo(
    () => ({
      estimators: data || [],
      estimatorsLoading: isLoading,
      estimatorsError: error,
      estimatorsValidating: isValidating,
      estimatorsEmpty: !isLoading && !(data && data.length),
      estimatorsRefetch: mutate,
    }),
    [data, error, isLoading, isValidating, mutate]
  );
}

// ----------------------------------------------------------------------

export function useGetEstimatorDetails(code, params) {
  const URL = endpoints.estimators.details(code);
  const swrKey = [URL, params];
  const {
    data, isLoading, error, isValidating, mutate,
  } = useSWR(swrKey, fetcher, SWR_OPTIONS);

  return useMemo(
    () => {
      const estimatorData = data ? [data] : [];
      return {
        estimatorDetails: estimatorData,
        estimatorDetailsLoading: isLoading,
        estimatorDetailsError: error,
        estimatorDetailsValidating: isValidating,
        estimatorDetailsEmpty: !isLoading && !estimatorData.length,
        estimatorDetailsRefetch: mutate,
      };
    },
    [data, error, isLoading, isValidating, mutate]
  );
}

// ----------------------------------------------------------------------

export function useGetEstimatorDefaultDetails(code) {
  const URL = endpoints.estimators.details(code);
  const {
    data, isLoading, error, isValidating, mutate,
  } = useSWR(URL, fetcher, SWR_OPTIONS);

  return useMemo(
    () => {
      const estimatorData = data ? [data] : [];
      return {
        estimatorDefaultDetails: estimatorData,
        estimatorDefaultDetailsLoading: isLoading,
        estimatorDefaultDetailsError: error,
        estimatorDefaultDetailsValidating: isValidating,
        estimatorDefaultDetailsEmpty: !isLoading && !estimatorData.length,
        estimatorDefaultDetailsRefetch: mutate,
      };
    },
    [data, error, isLoading, isValidating, mutate]
  );
}

// ----------------------------------------------------------------------

export function useGetQRCode() {
  const URL = endpoints.qrCode;
  const {
    data, isLoading, error, isValidating, mutate,
  } = useSWR(URL, fetcher, SWR_OPTIONS);

  return useMemo(
    () => ({
      qrCode: data || {},
      qrCodeLoading: isLoading,
      qrCodeError: error,
      qrCodeValidating: isValidating,
      qrCodeEmpty: !isLoading && !(data && data.length),
      qrCodeRefetch: mutate,
    }),
    [data, error, isLoading, isValidating, mutate]
  );
}

// ----------------------------------------------------------------------

export function useGetCameraList() {
  const URL = endpoints.camera.list;
  const {
    data, isLoading, error,
  } = useSWR(URL, fetcher, SWR_OPTIONS);

  return useMemo(
    () => ({
      cameraList: data || [],
      cameraListLoading: isLoading,
      cameraListError: error,
      cameraListEmpty: !isLoading && !(data && data.length),
    }),
    [data, error, isLoading]
  );
}
